import type { CurrentPostContextObject } from "@circle-react/contexts/currentPostContext";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isPublicCommunity } from "@circle-react/helpers/communityHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { dateStringToTimeAgo } from "@circle-react/helpers/dateTimeHelpers/timeAgo";
import { resolveMemberSince } from "@circle-react/helpers/memberSince";
import * as postHelpers from "@circle-react/helpers/postHelpers";
import {
  isPublicSpace,
  isSpaceMember,
} from "@circle-react/helpers/spaceHelpers";
import { useIsIframeEmbed } from "@circle-react/hooks/useIsIframeEmbed";
import type { Post } from "@circle-react/types/Post";
import { useMutation } from "react-query";

import { reactQueryPut } from "@/react/helpers/backendRequestHelpers";
import { usePostModalParams } from "@/react/hooks/usePostModalParams";
import type { Space } from "@/react/types/Space";

interface Options {
  refetch: false;
}

export interface UseCurrentPostHelpersProps {
  onChangeSharePost?: (post: Post, options: Options) => void;
}

interface MutationVariables {
  body: any;
  postId: string;
}

type PostContextObject = Omit<CurrentPostContextObject, "record">;

export interface UseCurrentPostHelpers extends PostContextObject {
  canDestroyPost: boolean;
  canDuplicatePost: boolean;
  canManagePost: boolean;
  canReportPost: boolean;
  canUpdatePost: boolean;
  getSharePostLink: (...args: any[]) => any;
  isBasicPost: boolean;
  isDeleteBlockedByPaidAttendees: boolean;
  isEventPost: boolean;
  isImagePost: boolean;
  isLoadingSharePostData: boolean;
  isPostBodyEmpty: boolean;
  isPostMetaHidden: boolean;
  isSharedPostView: boolean;
  isSharedPostViewWithMinimalLayout: boolean;
  isSharedPostWithTeaserLayout: boolean;
  isTiptapPost: boolean;
  isTrixPost: boolean;
  post?: any;
  posterJoinedDate: string;
  sharePostData?: any;
  shouldHideCommentsListForSpace: (arg: any) => boolean;
  shouldShowLeaveCommentButton: boolean;
  showEngagementActions: boolean;
  showGatedCommentSection: boolean;
  showGatedCommentSectionAtBottom: boolean;
  showGatedCommentSectionAtTop: boolean;
  showModerationInActionsDropdown: boolean;
  showPostActions: boolean;
  showShareOnWebOptions: (space: any) => boolean;
  showSpaceName: boolean;
}

const usePostPermissions = (post: any) => {
  const canManagePost = postHelpers.canManagePost(post);
  const canUpdatePost = postHelpers.canUpdatePost(post);
  const canDuplicatePost = postHelpers.canDuplicatePost(post);
  const isDeleteBlockedByPaidAttendees =
    postHelpers.isDeleteBlockedByPaidAttendees(post);
  const canDestroyPost = postHelpers.canDestroyPost(post);
  const canReportPost = postHelpers.canReportPost(post);

  return {
    canManagePost,
    canUpdatePost,
    canDuplicatePost,
    canDestroyPost,
    isDeleteBlockedByPaidAttendees,
    canReportPost,
  };
};

const usePostViewHelpers = (
  post: any,
  isIframeEmbed: boolean,
  currentUser: any,
  postSlug: string | undefined,
  canManagePost: boolean,
) => {
  const isSharedPostView = postHelpers.isSharedPostView(post, {
    postSlugInUrl: postSlug,
  });

  const isSharedPostViewWithMinimalLayout = Boolean(
    isSharedPostView && post.minimal_layout,
  );

  const isSharedPostWithTeaserLayout = Boolean(
    isSharedPostView && post.show_post_teaser,
  );

  const shouldShowPostActions =
    !isIframeEmbed &&
    postHelpers.showPostActions(post, {
      currentUser,
      postSlugInUrl: postSlug,
    });

  const shouldShowSpaceName = isSharedPostViewWithMinimalLayout;

  const shouldShowGatedCommentSection = Boolean(
    (!currentUser && post.is_comments_enabled && !post.is_comments_closed) ||
    isSharedPostWithTeaserLayout,
  );

  const shouldShowGatedCommentSectionAtTop =
    shouldShowGatedCommentSection && post.comments_count > 5;

  const shouldShowGatedCommentSectionAtBottom =
    shouldShowGatedCommentSection && post.comments_count <= 5;

  const shouldShowModerationInActionsDropdown = Boolean(
    post.flagged_for_approval_at && canManagePost,
  );

  const shouldShowEngagementActions = Boolean(
    !isSharedPostViewWithMinimalLayout &&
    (post.is_liking_enabled || post.is_comments_enabled),
  );

  return {
    isSharedPostView,
    isSharedPostViewWithMinimalLayout,
    isSharedPostWithTeaserLayout,
    showSpaceName: shouldShowSpaceName,
    showGatedCommentSection: shouldShowGatedCommentSection,
    showGatedCommentSectionAtBottom: shouldShowGatedCommentSectionAtBottom,
    showGatedCommentSectionAtTop: shouldShowGatedCommentSectionAtTop,
    showModerationInActionsDropdown: shouldShowModerationInActionsDropdown,
    showPostActions: shouldShowPostActions,
    showEngagementActions: shouldShowEngagementActions,
  };
};

export const useCurrentPostHelpers = ({
  onChangeSharePost,
}: UseCurrentPostHelpersProps = {}): UseCurrentPostHelpers => {
  const { postSlug } = usePostModalParams();
  const isIframeEmbed = useIsIframeEmbed();
  const { record: post, ...rest } = useCurrentPostContext();

  const posterJoinedDate = (() => {
    const joined = resolveMemberSince(
      post?.community_member?.member_since,
      post?.community_member?.created_at,
    );
    return joined ? dateStringToTimeAgo(joined) : "";
  })();

  const { currentUser, currentCommunity, currentCommunityMember } =
    usePunditUserContext();

  const postPermissions = usePostPermissions(post);
  const postViewHelpers = usePostViewHelpers(
    post,
    isIframeEmbed,
    currentUser,
    postSlug,
    postPermissions.canManagePost,
  );
  const showShareOnWebOptions = (space: Space) =>
    !postViewHelpers.isSharedPostView &&
    !isIframeEmbed &&
    postHelpers.canSharePost(post, currentCommunity, space);

  const isBasicPost = postHelpers.isBasicPost(post);
  const isEventPost = postHelpers.isEventPost(post);
  const isImagePost = postHelpers.isImagePost(post);
  const isTiptapPost = postHelpers.isUsingTiptapEditor(post);
  const isTrixPost = postHelpers.isUsingTrixEditor(post);
  const isPostBodyEmpty = postHelpers.isPostBodyEmpty(post);
  const isPostMetaHidden = postHelpers.isPostMetaHidden(post);

  const shouldShowLeaveCommentButton = Boolean(
    postViewHelpers.isSharedPostViewWithMinimalLayout && currentCommunityMember,
  );

  const shouldHideCommentsListForSpace = ({ space }: { space: Space }) => {
    const canAccessCommunity =
      isPublicCommunity(currentCommunity) || currentCommunityMember;
    const canAccessSpace =
      isPublicSpace(space) ||
      isSpaceMember(space) ||
      isCommunityAdmin(currentCommunityMember);
    const canAccessPost = canAccessCommunity && canAccessSpace;

    return (
      !canAccessPost ||
      postViewHelpers.isSharedPostViewWithMinimalLayout ||
      postViewHelpers.isSharedPostWithTeaserLayout
    );
  };

  const {
    mutate: getSharePostLink,
    data: sharePostData,
    isPending: isLoadingSharePostData,
  } = useMutation<Post, unknown, MutationVariables>({
    mutationFn: ({ postId, body }) =>
      reactQueryPut(`/posts/${postId}/share`, body),
    onSuccess: data => {
      if (onChangeSharePost) {
        onChangeSharePost(
          {
            ...post,
            ...data,
          },
          { refetch: false },
        );
      }
    },
  });

  return {
    post,
    ...rest,
    ...postPermissions,
    ...postViewHelpers,
    getSharePostLink,
    isBasicPost,
    isEventPost,
    isImagePost,
    isLoadingSharePostData,
    isPostBodyEmpty,
    isPostMetaHidden,
    isTiptapPost,
    isTrixPost,
    posterJoinedDate,
    sharePostData,
    shouldHideCommentsListForSpace,
    shouldShowLeaveCommentButton,
    showShareOnWebOptions,
  };
};
